import React, { useState, lazy } from 'react';
import { DollarSign, SparklesIcon, SettingsIcon, Leaf, InfoIcon } from 'lucide-react';
const RestaurantComparisonTable = () => {
  const [activeSection, setActiveSection] = useState('features');
  const [activeTooltip, setActiveTooltip] = useState(null);
  const handleTooltipToggle = id => {
    if (activeTooltip === id) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(id);
    }
  };
  // Data for comparison table
  const features = [{
    name: 'Premium Clean Label Ingredients',
    tooltip: 'High-quality ingredients with no artificial additives',
    smartsoda: {
      text: '✓',
      status: 'good'
    },
    fountain: {
      text: '✗',
      status: 'bad'
    },
    bottles: {
      text: '?',
      status: 'medium'
    }
  }, {
    name: 'Increase in Margin',
    tooltip: 'Additional profit margin on beverage sales',
    smartsoda: {
      text: '60%+',
      status: 'good'
    },
    fountain: {
      text: '0%',
      status: 'bad'
    },
    bottles: {
      text: '0%',
      status: 'bad'
    }
  }, {
    name: 'Storage Space Required',
    tooltip: 'Physical space needed for equipment and supplies',
    smartsoda: {
      text: 'Minimal',
      status: 'good'
    },
    fountain: {
      text: 'Moderate',
      status: 'medium'
    },
    bottles: {
      text: 'Extensive',
      status: 'bad'
    }
  }, {
    name: 'Alkaline Water',
    tooltip: 'pH-balanced water with health benefits',
    smartsoda: {
      text: '✓',
      status: 'good'
    },
    fountain: {
      text: '✗',
      status: 'bad'
    },
    bottles: {
      text: 'Limited options',
      status: 'medium'
    }
  }, {
    name: 'Automated inventory management',
    tooltip: 'System monitors and manages inventory levels',
    smartsoda: {
      text: '✓',
      status: 'good'
    },
    fountain: {
      text: '✗',
      status: 'bad'
    },
    bottles: {
      text: '✗',
      status: 'bad'
    }
  }, {
    name: 'Custom flavor options',
    tooltip: 'Ability to create signature flavor combinations',
    smartsoda: {
      text: '✓',
      status: 'good'
    },
    fountain: {
      text: '✗',
      status: 'bad'
    },
    bottles: {
      text: '✗',
      status: 'bad'
    }
  }, {
    name: 'Craft mocktail capabilities',
    tooltip: 'Create premium non-alcoholic mixed beverages',
    smartsoda: {
      text: '✓',
      status: 'good'
    },
    fountain: {
      text: '✗',
      status: 'bad'
    },
    bottles: {
      text: '✗',
      status: 'bad'
    }
  }, {
    name: 'Environmental impact',
    tooltip: 'Effect on environment including waste and carbon footprint',
    smartsoda: {
      text: 'Minimal',
      status: 'good'
    },
    fountain: {
      text: 'Moderate',
      status: 'medium'
    },
    bottles: {
      text: 'High',
      status: 'bad'
    }
  }, {
    name: 'Usage analytics',
    tooltip: 'Data on consumption patterns and preferences',
    smartsoda: {
      text: '✓',
      status: 'good'
    },
    fountain: {
      text: '✗',
      status: 'bad'
    },
    bottles: {
      text: '✗',
      status: 'bad'
    }
  }];
  // Helper function to render status indicator
  const renderStatus = (status, text) => {
    if (status === 'good') return <span className="text-green-600" data-id="element-994">{text}</span>;
    if (status === 'medium') return <span className="text-yellow-500" data-id="element-995">{text}</span>;
    if (status === 'bad') return <span className="text-red-500" data-id="element-996">{text}</span>;
    return null;
  };
  return <>
      <section className="py-20 bg-gray-50" id="comparison" data-id="element-997">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-998">
          <div className="text-center mb-16" data-id="element-999">
            <h2 className="text-3xl font-bold text-navy-950 sm:text-4xl" data-id="element-1000">
              How SmartSoda Compares for Restaurants
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1001">
              See why SmartSoda is the premium choice for taste, sustainability,
              and profitability in restaurant settings
            </p>
          </div>
          {/* Desktop/Tablet View - Standard Table */}
          <div className="hidden md:block overflow-x-auto" data-id="element-1002">
            <table className="min-w-full bg-white rounded-xl shadow-md overflow-hidden" data-id="element-1003">
              <thead data-id="element-1004">
                <tr className="bg-blue-50" data-id="element-1005">
                  <th className="px-6 py-4 text-left text-navy-950 font-semibold text-lg border-b" data-id="element-1006">
                    Features
                  </th>
                  <th className="px-6 py-4 text-center text-navy-950 font-semibold text-lg border-b" data-id="element-1007">
                    <span className="flex flex-col items-center" data-id="element-1008">
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fBPdfjbGq4JwnBvAThJbWB/SS-Horizontal-Lockup_River-Royale.webp" alt="SmartSoda" className="h-12 mb-2 object-contain" width="150" height="48" loading="lazy" decoding="async" data-id="element-1009" />
                    </span>
                  </th>
                  <th className="px-6 py-4 text-center text-navy-950 font-semibold text-lg border-b" data-id="element-1010">
                    Traditional Fountain
                  </th>
                  <th className="px-6 py-4 text-center text-navy-950 font-semibold text-lg border-b" data-id="element-1011">
                    Bottles/Cans
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1012">
                {features.map((feature, idx) => <tr key={`feature-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-1013">
                    <td className="px-6 py-4 border-b text-navy-950 font-medium" data-id="element-1014">
                      {feature.name}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.smartsoda.status === 'good' ? 'text-green-600' : feature.smartsoda.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-1015">
                      {feature.smartsoda.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.fountain.status === 'good' ? 'text-green-600' : feature.fountain.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-1016">
                      {feature.fountain.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.bottles.status === 'good' ? 'text-green-600' : feature.bottles.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-1017">
                      {feature.bottles.text}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {/* Mobile View - Card-based Layout */}
          <div className="md:hidden" data-id="element-1018">
            {/* Mobile feature cards */}
            <div className="space-y-4" data-id="element-1019">
              {features.map((feature, idx) => <div key={`mobile-feature-${idx}`} className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-1020">
                  <div className="px-4 py-3 bg-blue-50 flex items-center justify-between" data-id="element-1021">
                    <h3 className="font-medium text-navy-950" data-id="element-1022">
                      {feature.name}
                    </h3>
                    <button onClick={() => handleTooltipToggle(`mobile-feature-${idx}`)} className="text-gray-400" aria-label={`Info about ${feature.name}`} data-id="element-1023">
                      <InfoIcon className="h-4 w-4" data-id="element-1024" />
                    </button>
                  </div>
                  {activeTooltip === `mobile-feature-${idx}` && <div className="px-4 py-2 bg-blue-50 text-sm text-gray-700 border-t border-blue-100" data-id="element-1025">
                      {feature.tooltip}
                    </div>}
                  <div className="divide-y divide-gray-100" data-id="element-1026">
                    <div className="px-4 py-3 flex justify-between items-center" data-id="element-1027">
                      <div className="font-medium" data-id="element-1028">SmartSoda</div>
                      <div className={`${feature.smartsoda.status === 'good' ? 'text-green-600' : feature.smartsoda.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-1029">
                        {feature.smartsoda.text}
                      </div>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center" data-id="element-1030">
                      <div className="font-medium" data-id="element-1031">Traditional Fountain</div>
                      <div className={`${feature.fountain.status === 'good' ? 'text-green-600' : feature.fountain.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-1032">
                        {feature.fountain.text}
                      </div>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center" data-id="element-1033">
                      <div className="font-medium" data-id="element-1034">Bottles/Cans</div>
                      <div className={`${feature.bottles.status === 'good' ? 'text-green-600' : feature.bottles.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-1035">
                        {feature.bottles.text}
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          <div className="mt-12 text-center" data-id="element-1036">
            <a href="#book-demo" className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-lg shadow-md text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all" data-id="element-1037">
              Experience the SmartSoda Difference
            </a>
          </div>
        </div>
      </section>
      <section className="py-20 bg-white" data-id="element-1038">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1039">
          <div className="text-center mb-12" data-id="element-1040">
            <h3 className="text-2xl font-bold text-navy-950 mb-8" data-id="element-1041">
              The Restaurant Advantage
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1042">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all hover:transform hover:scale-105" data-id="element-1043">
              <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mb-6 mx-auto" data-id="element-1044">
                <DollarSign className="h-8 w-8 text-amber-600" data-id="element-1045" />
              </div>
              <h3 className="text-xl font-semibold text-center text-navy-950 mb-4" data-id="element-1046">
                Higher Check Averages
              </h3>
              <p className="text-gray-600 text-center" data-id="element-1047">
                Premium beverages command higher prices, increasing your average
                check size and overall revenue.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all hover:transform hover:scale-105" data-id="element-1048">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mb-6 mx-auto" data-id="element-1049">
                <SparklesIcon className="h-8 w-8 text-purple-600" data-id="element-1050" />
              </div>
              <h3 className="text-xl font-semibold text-center text-navy-950 mb-4" data-id="element-1051">
                Brand Differentiation
              </h3>
              <p className="text-gray-600 text-center" data-id="element-1052">
                Set your restaurant apart with unique beverage offerings that
                competitors can't match.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all hover:transform hover:scale-105" data-id="element-1053">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mb-6 mx-auto" data-id="element-1054">
                <SettingsIcon className="h-8 w-8 text-blue-600" data-id="element-1055" />
              </div>
              <h3 className="text-xl font-semibold text-center text-navy-950 mb-4" data-id="element-1056">
                Operational Efficiency
              </h3>
              <p className="text-gray-600 text-center" data-id="element-1057">
                Eliminate bottle/can storage, reduce waste management, and
                streamline beverage service during busy periods.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all hover:transform hover:scale-105" data-id="element-1058">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mb-6 mx-auto" data-id="element-1059">
                <Leaf className="h-8 w-8 text-green-600" data-id="element-1060" />
              </div>
              <h3 className="text-xl font-semibold text-center text-navy-950 mb-4" data-id="element-1061">
                Sustainability Story
              </h3>
              <p className="text-gray-600 text-center" data-id="element-1062">
                Enhance your restaurant's environmental credentials with a
                zero-waste beverage program that resonates with eco-conscious
                diners.
              </p>
            </div>
          </div>
          <div className="mt-12 text-center" data-id="element-1063">
            <a href="#book-demo" className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-lg shadow-md text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all" data-id="element-1064">
              Schedule Your Restaurant Demo
            </a>
          </div>
        </div>
      </section>
    </>;
};
export default RestaurantComparisonTable;